import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useParsedProducts } from '@lib/core/products/hooks/useParsedProducts';
import { useProductFeedback } from '@lib/core/products/hooks/useProductFeedback';
import { selectActiveTab, selectIsLoading, selectTabsConfig } from '@lib/core/products/selectors/resultCatalog';
import { resetResultCatalog } from '@lib/core/products/slices/resultCatalog';
import { prependBasename } from '@lib/core/service/utils';
import { useProductList, useProductRate } from '@lib/core/users/hooks';
import { resetCatalogFilters } from '@lib/tools/catalogFilter/slices/catalogFilter';
import { useAddons } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';
import { PAGES } from '@lib/tools/views/urls';

export const useResultCatalog = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();

  const { isEventsGamingAddon, isFreeFormTastingAddon } = useAddons();
  const isGameAddon = isEventsGamingAddon || isFreeFormTastingAddon;

  const { isWishlistProductListLoading } = useProductList();
  const { isProductRateUpdateLoading } = useProductRate();
  const { parseProductsArray } = useParsedProducts();
  const { isProductFeedbackCompleted } = useProductFeedback();

  const activeTabCode = useSelector(selectActiveTab);
  const tabsConfig = useSelector(selectTabsConfig);
  const isProductsLoading = useSelector(selectIsLoading);

  const activeTab = useMemo(() => {
    return tabsConfig.find(tab => tab.tabCode === activeTabCode) ?? null;
  }, [tabsConfig, activeTabCode]);

  const activeTabParsedProducts = useMemo(() => {
    return activeTab?.products ? parseProductsArray(activeTab?.products) : [];
  }, [activeTab?.products, isProductFeedbackCompleted, isWishlistProductListLoading, isProductRateUpdateLoading]);

  const activeTabHasMoreProducts = activeTab?.hasMore ?? true;
  const isCustomTab = activeTab?.tabType === 'custom';

  const shouldLoadActiveTabProducts = activeTab && activeTab?.products === null && !isCustomTab && !isProductsLoading;

  const handleResetAndNavigateResultCatalog = () => {
    const page = isGameAddon ? PAGES.vinhood.action : PAGES.vinhood.catalog;

    dispatch(resetCatalogFilters());
    dispatch(resetResultCatalog());
    navigate(prependBasename(page));
  };

  return useMemo(
    () => ({
      activeTabCode,
      activeTabHasMoreProducts,
      activeTabParsedProducts,
      handleResetAndNavigateResultCatalog,
      isProductsLoading,
      shouldLoadActiveTabProducts,
      tabsConfig,
    }),
    [activeTabCode, activeTabHasMoreProducts, activeTabParsedProducts, isProductsLoading, tabsConfig],
  );
};
